/**
 * Cliente REST. Uma função `request` e um mapa de rotas — nada de fábricas nem
 * de camada de "serviços" por cima: o que a API expõe está aqui em cru, com os
 * tipos de `@greenvoice/shared`, e é isso que faz o compilador apanhar uma rota
 * que mudou de forma.
 *
 * Autenticação: cookie `gv_token` httpOnly. O cliente NUNCA vê o token — por
 * isso todos os pedidos vão com `credentials: "include"` e nunca há um header
 * Authorization a montar. Um XSS aqui não consegue roubar a sessão.
 */

import type {
  AuditEntry,
  Channel,
  ChannelType,
  Emoji,
  Gender,
  Invite,
  InvitePreview,
  Member,
  Message,
  MessageSearchPage,
  Mute,
  PontoRegistro,
  PontoTipo,
  PresenceStatus,
  PublicUser,
  Relationship,
  Role,
  SelfUser,
  Server,
  Snowflake,
  VoiceState,
} from "@greenvoice/shared";

import { resolveHost } from "./host";

// `resolveHost` faz o `localhost` do `.env` seguir o IP/domínio de onde a página
// foi mesmo aberta — sem isto, um colega na LAN pedia à API da máquina DELE.
// Um `PUBLIC_API_URL=/` (o que a ponte usa) cai em BASE vazia: os pedidos ficam
// relativos, same-origin, e é o proxy da frente que os leva à API.
const BASE = resolveHost(import.meta.env.PUBLIC_API_URL ?? "http://localhost:4000");

/** Erro com o status HTTP à mão — a UI decide entre "sessão caiu" e "falhou". */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 401: o cookie expirou ou foi revogado. A UI volta ao ecrã de entrada. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * 403 com `code: "ADMIN_ONLY"`: a pessoa não tem cadastro no painel Green
   * Village. É o único erro de entrada que NÃO é "tenta outra vez" — não há
   * password nenhuma que a faça passar, por isso a UI mostra outra coisa.
   */
  get isAdminOnly(): boolean {
    return this.status === 403 && this.field("code") === "ADMIN_ONLY";
  }

  /** Segunda linha que a API às vezes junta, a dizer o que fazer a seguir. */
  get hint(): string | null {
    return this.field("hint");
  }

  private field(name: string): string | null {
    if (typeof this.body !== "object" || this.body === null) return null;
    const value = (this.body as Record<string, unknown>)[name];
    return typeof value === "string" ? value : null;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

function url(path: string, query?: Query): string {
  const full = `${BASE}${path}`;
  if (!query) return full;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${full}?${qs}` : full;
}

async function request<T>(method: string, path: string, options: { body?: unknown; query?: Query } = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url(path, options.query), {
      method,
      credentials: "include",
      headers: options.body === undefined ? {} : { "content-type": "application/json" },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch (cause) {
    // fetch só rejeita por rede/CORS. Distinguir isto de um 500 importa: um é
    // "tenta outra vez", o outro é "a API tem um bug".
    throw new ApiError(0, "Sem ligação à API.", cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, messageOf(payload) ?? response.statusText, payload);
  }
  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** O Nest devolve `{ message }` — string ou array, conforme o filtro. */
function messageOf(payload: unknown): string | undefined {
  if (typeof payload !== "object" || payload === null) return undefined;
  const message = (payload as { message?: unknown }).message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.filter((m) => typeof m === "string").join(" ");
  return undefined;
}

const get = <T>(path: string, query?: Query) => request<T>("GET", path, query ? { query } : {});
const post = <T>(path: string, body?: unknown) => request<T>("POST", path, { body });
const patch = <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body });
const put = <T>(path: string, body?: unknown) => request<T>("PUT", path, { body });
const del = <T>(path: string, body?: unknown) => request<T>("DELETE", path, { body });

export interface AuthSuccess {
  user: SelfUser;
  token: string;
  sessionId: string;
  expiresAt: string;
}
export type AuthResult = AuthSuccess | { mfaRequired: true };

/**
 * Como este servidor deixa entrar. Vem do servidor porque é ele que sabe: o
 * mesmo código corre num servidor aberto (dev) e num fechado ao painel admin
 * (produção), e o ecrã de entrada não pode adivinhar em qual está.
 */
export interface AuthPolicy {
  /** Só entra quem tem cadastro activo no painel Green Village. */
  adminGate: boolean;
  /** Ainda se pode criar conta aqui. */
  openRegistration: boolean;
}

export interface SessionInfo {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string;
  current: boolean;
}

export interface VoiceCapabilities {
  canSpeak: boolean;
  canVideo: boolean;
  canShareScreen: boolean;
  canRecord: boolean;
}

export interface VoiceJoinResult {
  token: string;
  url: string;
  roomName: string;
  capabilities: VoiceCapabilities;
}

/**
 * O bilhete de upload. Estes campos são os que o servidor manda mesmo — foram
 * declarados uma vez de cabeça e o `headers` não existia do outro lado, o que
 * matava todos os anexos no `Object.entries`. Mexer aqui sem mexer no
 * `UploadIntent` da API volta a partir tudo em silêncio; há um teste que
 * compara os dois lados linha a linha.
 */
export interface UploadTicket {
  fileId: Snowflake;
  uploadUrl: string;
  method?: string;
  /**
   * Pôr no PUT tal e qual: é assim que o objecto fica com o tipo certo.
   *
   * Opcional só por uma razão de implantação: a API corre compilada e só troca
   * de versão quando a ponte reinicia, enquanto isto recarrega a cada gravação
   * — há sempre uma janela com o cliente novo contra o servidor antigo. Nessa
   * janela `uploadHeaders` deriva o header do `contentType` do MESMO bilhete.
   * O servidor manda-o sempre; quem garante que continua a mandar é o teste que
   * compara esta interface com a `UploadIntent`, campo a campo.
   */
  headers?: Record<string, string>;
  contentType: string;
  expiresInSeconds: number;
}

/** Finalidade do upload — decide a pasta, o limite de tamanho e o `claimImage`. */
export type FileKind = "ATTACHMENT" | "AVATAR" | "BANNER" | "ICON" | "EMOJI";

/**
 * Um banimento como o servidor o devolve.
 *
 * Isto estava declarado como `{ userId, reason }` — metade do que a API manda.
 * O `listBans` faz `include` do utilizador de propósito para a lista poder
 * mostrar gente em vez de snowflakes; sem os nomes aqui, o ecrã de desbanir
 * seria uma coluna de números e ninguém saberia quem estava a desbanir.
 */
export interface BanEntry {
  userId: Snowflake;
  username: string;
  displayName: string | null;
  /** Quem baniu. Pode não ser membro hoje — resolve-se pelo cache, se der. */
  issuerId: Snowflake;
  reason: string | null;
  createdAt: string;
}

export const api = {
  auth: {
    policy: () => get<AuthPolicy>("/auth/policy"),
    register: (body: {
      username: string;
      email: string;
      password: string;
      displayName?: string;
      gender: "MALE" | "FEMALE";
    }) => post<AuthResult>("/auth/register", body),
    login: (body: { login: string; password: string; mfaCode?: string }) => post<AuthResult>("/auth/login", body),
    logout: () => post<void>("/auth/logout"),
    logoutAll: () => post<{ terminated: number }>("/auth/logout-all"),
    sessions: () => get<SessionInfo[]>("/auth/sessions"),
    revokeSession: (id: string) => del<void>(`/auth/sessions/${id}`),
    changePassword: (body: { current: string; next: string }) => post<void>("/auth/password", body),
  },

  users: {
    self: () => get<SelfUser>("/users/@me"),
    updateSelf: (body: {
      username?: string;
      displayName?: string | null;
      bio?: string | null;
      // O backend liga o avatar/banner pelo id do ficheiro já enviado (fá-lo
      // `claim` como imagem), não por um URL — mandar um URL era um no-op.
      avatarId?: string | null;
      bannerId?: string | null;
      status?: PresenceStatus;
      statusText?: string | null;
      /** O perfil que noutro sítio se paga: a cor é um hexadecimal de seis
       *  dígitos (o servidor recusa o resto) e a moldura é um id de
       *  `AVATAR_FRAMES`. `null` em qualquer um deles é "tirar". */
      accentColor?: string | null;
      avatarFrame?: string | null;
      /** A personagem do escritório 2D. Guardar também MARCA que se escolheu —
       *  é o que impede o ecrã de escolha de voltar na entrada seguinte. */
      gender?: Gender;
      /** Onde a capa é cortada, 0-100. `null` volta ao meio. */
      bannerPos?: number | null;
    }) => patch<SelfUser>("/users/@me", body),
    get: (userId: Snowflake) => get<PublicUser>(`/users/${userId}`),
    relationships: () => get<Relationship[]>("/users/@me/relationships"),
    requestFriend: (username: string) => post<Relationship>("/users/@me/relationships", { username }),
    acceptFriend: (userId: Snowflake) => put<Relationship>(`/users/@me/relationships/${userId}`),
    removeFriend: (userId: Snowflake) => del<void>(`/users/@me/relationships/${userId}`),
    block: (userId: Snowflake) => put<Relationship>(`/users/@me/blocks/${userId}`),
    channels: () => get<Channel[]>("/users/@me/channels"),
    createChannel: (body: { recipientIds: Snowflake[]; name?: string }) => post<Channel>("/users/@me/channels", body),
  },

  servers: {
    list: () => get<Server[]>("/servers"),
    create: (body: { name: string; description?: string }) => post<Server>("/servers", body),
    get: (serverId: Snowflake) => get<Server>(`/servers/${serverId}`),
    update: (
      serverId: Snowflake,
      body: Partial<Pick<Server, "name" | "description" | "iconUrl" | "bannerUrl" | "systemChannelId">>,
    ) => patch<Server>(`/servers/${serverId}`, body),
    remove: (serverId: Snowflake) => del<void>(`/servers/${serverId}`),
    leave: (serverId: Snowflake) => del<void>(`/servers/${serverId}/members/@me`),
    members: (serverId: Snowflake, query?: { after?: Snowflake; limit?: number }) =>
      get<Member[]>(`/servers/${serverId}/members`, query),
    member: (serverId: Snowflake, userId: Snowflake) => get<Member>(`/servers/${serverId}/members/${userId}`),
    updateMember: (
      serverId: Snowflake,
      userId: Snowflake,
      body: { nickname?: string | null; roleIds?: Snowflake[]; timeoutUntil?: string | null },
    ) => patch<Member>(`/servers/${serverId}/members/${userId}`, body),
    kick: (serverId: Snowflake, userId: Snowflake) => del<void>(`/servers/${serverId}/members/${userId}`),
    bans: (serverId: Snowflake) => get<BanEntry[]>(`/servers/${serverId}/bans`),
    // `deleteMessageDays` 0-7: apaga o rasto de mensagens dessa pessoa em todos
    // os canais do servidor. O controlador valida o intervalo; 0 é não apagar.
    ban: (serverId: Snowflake, userId: Snowflake, body?: { reason?: string; deleteMessageDays?: number }) =>
      put<void>(`/servers/${serverId}/bans/${userId}`, body ?? {}),
    unban: (serverId: Snowflake, userId: Snowflake) => del<void>(`/servers/${serverId}/bans/${userId}`),
    roles: (serverId: Snowflake) => get<Role[]>(`/servers/${serverId}/roles`),
    createRole: (serverId: Snowflake, body: { name: string; color?: string | null }) =>
      post<Role>(`/servers/${serverId}/roles`, body),
    updateRole: (serverId: Snowflake, roleId: Snowflake, body: Partial<Role>) =>
      patch<Role>(`/servers/${serverId}/roles/${roleId}`, body),
    deleteRole: (serverId: Snowflake, roleId: Snowflake) => del<void>(`/servers/${serverId}/roles/${roleId}`),
    reorderRoles: (serverId: Snowflake, order: { id: Snowflake; rank: number }[]) =>
      patch<Role[]>(`/servers/${serverId}/roles/order`, { order }),
    auditLog: (serverId: Snowflake, query?: { before?: Snowflake; limit?: number; action?: string }) =>
      get<AuditEntry[]>(`/servers/${serverId}/audit-log`, query),
    /**
     * Procura em TODOS os canais do servidor que esta pessoa pode ler — a lista
     * é resolvida no servidor, não se manda daqui. Fica em `servers` e não em
     * `messages` porque atravessa canais: o resultado traz mensagens de sítios
     * diferentes, e `messages.*` é sempre dentro de um canal.
     */
    searchMessages: (
      serverId: Snowflake,
      query: { q: string; channelId?: Snowflake; authorId?: Snowflake; before?: Snowflake; limit?: number },
    ) => get<MessageSearchPage>(`/servers/${serverId}/messages/search`, query),
    createChannel: (
      serverId: Snowflake,
      body: { name: string; type: ChannelType; parentId?: Snowflake | null; topic?: string },
    ) => post<Channel>(`/servers/${serverId}/channels`, body),
    reorderChannels: (serverId: Snowflake, order: { id: Snowflake; position: number; parentId?: Snowflake | null }[]) =>
      patch<Channel[]>(`/servers/${serverId}/channels/order`, { order }),
    invites: (serverId: Snowflake) => get<Invite[]>(`/servers/${serverId}/invites`),
  },

  channels: {
    get: (channelId: Snowflake) => get<Channel>(`/channels/${channelId}`),
    update: (
      channelId: Snowflake,
      body: Partial<
        Pick<Channel, "name" | "topic" | "position" | "nsfw" | "slowmodeSeconds" | "userLimit" | "bitrate" | "parentId">
      >,
    ) => patch<Channel>(`/channels/${channelId}`, body),
    remove: (channelId: Snowflake) => del<void>(`/channels/${channelId}`),
    setOverwrite: (
      channelId: Snowflake,
      targetId: Snowflake,
      body: { targetType: "ROLE" | "MEMBER"; allow: string; deny: string },
    ) => put<Channel>(`/channels/${channelId}/permissions/${targetId}`, body),
    clearOverwrite: (channelId: Snowflake, targetId: Snowflake) =>
      del<Channel>(`/channels/${channelId}/permissions/${targetId}`),
    invites: (channelId: Snowflake) => get<Invite[]>(`/channels/${channelId}/invites`),
    createInvite: (channelId: Snowflake, body?: { maxUses?: number | null; expiresInSeconds?: number | null }) =>
      post<Invite>(`/channels/${channelId}/invites`, body ?? {}),
  },

  /**
   * Tópicos. Devolvem `Channel` porque um tópico É um canal — a mesma entidade,
   * com `type: "THREAD"` e `parentId` a apontar para o canal de onde saiu. É por
   * isso que não há `threads.remove`: fecha-se com `channels.remove`, pela mesma
   * rota com que se apaga um canal, e é o servidor que sabe que ali o dono
   * também pode.
   *
   * `listThreads` não pagina: o tecto são 100 por canal, e uma lista que nunca
   * passa do tecto trazia mais estado ao cliente do que resolvia.
   */
  threads: {
    list: (channelId: Snowflake) => get<Channel[]>(`/channels/${channelId}/threads`),
    /** `messageId` presente = o tópico nasce daquela mensagem (um por mensagem). */
    create: (channelId: Snowflake, body: { name: string; messageId?: Snowflake | null }) =>
      post<Channel>(`/channels/${channelId}/threads`, body),
  },

  /**
   * Silêncio pessoal. `PUT`/`DELETE` sobre um sub-recurso e não um `toggle`:
   * escrever duas vezes dá o mesmo resultado que escrever uma, e dois
   * separadores abertos não se desencontram a alternar cada um do seu estado.
   *
   * `minutes` conta a partir de AGORA e é o servidor que carimba a data final —
   * um relógio adiantado neste browser dava um silêncio já expirado ao nascer.
   * `null` (ou ausente) = para sempre.
   */
  mutes: {
    muteChannel: (channelId: Snowflake, minutes?: number | null) =>
      put<Mute>(`/channels/${channelId}/mute`, { minutes: minutes ?? null }),
    unmuteChannel: (channelId: Snowflake) => del<void>(`/channels/${channelId}/mute`),
    muteServer: (serverId: Snowflake, minutes?: number | null) =>
      put<Mute>(`/servers/${serverId}/mute`, { minutes: minutes ?? null }),
    unmuteServer: (serverId: Snowflake) => del<void>(`/servers/${serverId}/mute`),
  },

  /**
   * Emojis personalizados. O `create` recebe o id de um ficheiro JÁ carregado
   * (via `uploadImage(file, "EMOJI")`) e não os bytes: o upload vai direito ao
   * armazenamento, e a API só confirma que o ficheiro é desta pessoa e é uma
   * imagem. `rename` é `PATCH` porque o nome é o único campo que muda — trocar
   * a imagem é criar outro emoji.
   */
  emojis: {
    list: (serverId: Snowflake) => get<Emoji[]>(`/servers/${serverId}/emojis`),
    create: (serverId: Snowflake, body: { name: string; fileId: Snowflake }) =>
      post<Emoji>(`/servers/${serverId}/emojis`, body),
    rename: (serverId: Snowflake, emojiId: Snowflake, name: string) =>
      patch<Emoji>(`/servers/${serverId}/emojis/${emojiId}`, { name }),
    remove: (serverId: Snowflake, emojiId: Snowflake) => del<void>(`/servers/${serverId}/emojis/${emojiId}`),
  },

  invites: {
    resolve: (code: string) => get<InvitePreview>(`/invites/${code}`),
    join: (code: string) => post<Server>(`/invites/${code}`),
    remove: (code: string) => del<void>(`/invites/${code}`),
  },

  messages: {
    // `around` é a janela para saltar a um resultado da procura: metade das
    // mensagens antes, metade depois, e a própria incluída.
    list: (
      channelId: Snowflake,
      query?: { before?: Snowflake; after?: Snowflake; around?: Snowflake; limit?: number },
    ) => get<Message[]>(`/channels/${channelId}/messages`, query),
    send: (
      channelId: Snowflake,
      body: { content?: string; attachmentIds?: Snowflake[]; replyToId?: Snowflake | null },
    ) => post<Message>(`/channels/${channelId}/messages`, body),
    get: (channelId: Snowflake, messageId: Snowflake) => get<Message>(`/channels/${channelId}/messages/${messageId}`),
    edit: (channelId: Snowflake, messageId: Snowflake, content: string) =>
      patch<Message>(`/channels/${channelId}/messages/${messageId}`, { content }),
    remove: (channelId: Snowflake, messageId: Snowflake) => del<void>(`/channels/${channelId}/messages/${messageId}`),
    pins: (channelId: Snowflake) => get<Message[]>(`/channels/${channelId}/messages/pins`),
    pin: (channelId: Snowflake, messageId: Snowflake) =>
      put<Message>(`/channels/${channelId}/messages/${messageId}/pin`),
    unpin: (channelId: Snowflake, messageId: Snowflake) =>
      del<Message>(`/channels/${channelId}/messages/${messageId}/pin`),
    bulkDelete: (channelId: Snowflake, messageIds: Snowflake[]) =>
      post<void>(`/channels/${channelId}/messages/bulk-delete`, { messageIds }),
    react: (channelId: Snowflake, messageId: Snowflake, emoji: string) =>
      put<void>(`/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/@me`),
    unreact: (channelId: Snowflake, messageId: Snowflake, emoji: string, targetId = "@me") =>
      del<void>(`/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/${targetId}`),
    ack: (channelId: Snowflake, messageId: Snowflake) => post<void>(`/channels/${channelId}/messages/${messageId}/ack`),
  },

  voice: {
    join: (channelId: Snowflake) => post<VoiceJoinResult>(`/channels/${channelId}/voice/join`),
    /**
     * Huddle de corredor: sem canal, o servidor confirma o grupo pela geometria.
     *
     * O `spaceId` vai ESCAPADO. Hoje é o id do servidor nu e não precisava, mas
     * houve um segundo mundo com espaço `<serverId>#campus`, e um cardinal cru
     * num URL é o início da fragment: o pedido saía como `/spaces/<serverId>` e
     * o resto do caminho desaparecia sem erro nenhum do lado do cliente. O
     * escape fica — é grátis, e um cliente antigo ainda manda esse id.
     */
    joinHuddle: (spaceId: Snowflake, anchorId: Snowflake) =>
      post<VoiceJoinResult>(`/spaces/${encodeURIComponent(spaceId)}/huddle/${anchorId}/join`),
    // A sala de voz de uma ligação privada atendida — só as duas pontas entram.
    joinLigacao: (spaceId: Snowflake, ligacaoId: string) =>
      post<VoiceJoinResult>(`/spaces/${encodeURIComponent(spaceId)}/ligacao/${encodeURIComponent(ligacaoId)}/join`),
    leave: (channelId: Snowflake) => post<void>(`/channels/${channelId}/voice/leave`),
    updateState: (
      channelId: Snowflake,
      body: { selfMute?: boolean; selfDeaf?: boolean; screenSharing?: boolean; cameraOn?: boolean },
    ) => post<void>(`/channels/${channelId}/voice/state`, body),
    states: (channelId: Snowflake) => get<VoiceState[]>(`/channels/${channelId}/voice/states`),
    moderate: (userId: Snowflake, body: { serverMute?: boolean; serverDeaf?: boolean }) =>
      post<void>(`/voice/${userId}/moderate`, body),
    move: (userId: Snowflake, channelId: Snowflake) => post<void>(`/voice/${userId}/move`, { channelId }),
    disconnect: (userId: Snowflake) => post<void>(`/voice/${userId}/disconnect`),
  },

  files: {
    createUpload: (body: { filename: string; contentType: string; size: number; kind?: FileKind }) =>
      post<UploadTicket>("/files", body),
    complete: (fileId: Snowflake) => post<{ id: Snowflake; url: string }>(`/files/${fileId}/complete`),
    get: (fileId: Snowflake) => get<{ id: Snowflake; url: string }>(`/files/${fileId}`),
    remove: (fileId: Snowflake) => del<void>(`/files/${fileId}`),
  },

  push: {
    // `enabled` diz se o servidor tem VAPID configurado; sem isso `publicKey` é
    // null e o cliente nem tenta subscrever. A subscrição é do dispositivo.
    vapid: () => get<{ enabled: boolean; publicKey: string | null }>("/push/vapid"),
    subscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      post<void>("/push/subscribe", body),
    unsubscribe: (endpoint: string) => post<void>("/push/unsubscribe", { endpoint }),
  },

  ponto: {
    // O ponto é do painel admin; a API daqui só faz de ponte (`ponto.controller.ts`).
    meus: () => get<{ registros: PontoRegistro[] }>("/ponto/@me"),
    // O navegador só diz o FUSO — a hora da batida é a do servidor.
    bater: (tipo: PontoTipo, timeZone: string) =>
      post<{ registros: PontoRegistro[] }>("/ponto/@me", { tipo, timeZone }),
  },
};
